import { Link } from "@inertiajs/react"
import { AlertTriangle } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import { cn } from "@/lib/utils"

import { StatusChip, WaitingOnCell } from "./status-chip"
import type { WorkOrderRow } from "./types"

/**
 * The bad-order pipeline as seven columns, which is how the team already talks
 * about it ("it's sitting in dispo", "it's at the shop"). The table answers
 * "what is the state of THIS car"; the board answers "where is the work piling
 * up", and those are different questions asked by different people.
 *
 * Bad orders only. The other four work-order types have two or three states and
 * a board of them would be a list with extra whitespace.
 */
export function StageBoard({
  stages,
  workOrders,
}: {
  stages: { key: string; label: string }[]
  workOrders: WorkOrderRow[]
}) {
  const grouped = new Map<string, WorkOrderRow[]>()
  for (const stage of stages) grouped.set(stage.key, [])
  for (const row of workOrders) {
    const bucket = grouped.get(row.stage ?? "reported")
    if (bucket) bucket.push(row)
  }
  // Longest-sitting card at the top of each column, same instinct as the table.
  for (const bucket of grouped.values()) {
    bucket.sort((a, b) => (b.days_in_stage ?? 0) - (a.days_in_stage ?? 0))
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {stages.map((stage) => {
        const rows = grouped.get(stage.key) ?? []
        return (
          <div key={stage.key} className="w-64 shrink-0">
            <div className="mb-2 flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold">{stage.label}</h2>
              <Badge variant="secondary">{rows.length}</Badge>
            </div>
            <div className="bg-muted/40 min-h-24 space-y-2 rounded-lg p-2">
              {rows.length === 0 && (
                <p className="text-muted-foreground p-2 text-xs">Empty</p>
              )}
              {rows.map((row) => (
                <StageCard key={row.id} row={row} />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function StageCard({ row }: { row: WorkOrderRow }) {
  const routes = useOrgRoutes()

  return (
    <Link
      href={routes.workOrderPath(row.id)}
      className={cn(
        "bg-background hover:border-primary/50 block rounded-md border p-2.5 shadow-xs transition-colors",
        row.pod_missed && "border-destructive/50",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">
          {row.car_mark_number ?? `WO #${row.id}`}
        </span>
        {row.days_in_stage != null && (
          <span
            className={cn(
              "text-xs tabular-nums",
              row.days_in_stage >= 30
                ? "text-destructive font-semibold"
                : "text-muted-foreground",
            )}
            title="Days in this stage"
          >
            {row.days_in_stage}d
          </span>
        )}
      </div>

      {row.summary && (
        <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
          {row.summary}
        </p>
      )}

      <div className="mt-2 flex items-center justify-between gap-2">
        <WaitingOnCell
          waiting_on_label={row.waiting_on_label}
          days_waiting={row.days_waiting}
          className="text-xs"
        />
        {row.pod_missed && (
          <AlertTriangle
            className="text-destructive size-3.5"
            aria-label="Promised-out date missed"
          />
        )}
      </div>

      {row.shop_name && (
        <p className="text-muted-foreground mt-1 truncate text-xs">
          {row.shop_name}
        </p>
      )}

      <StatusChip
        status_label={row.status_label}
        tone={row.tone}
        className="mt-2"
      />
    </Link>
  )
}
